import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Icon,
  SimpleGrid,
  Divider,
  List,
  ListItem,
  ListIcon,
  Grid,
  GridItem,
  Flex,
  Center,
  Badge,
} from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import {
  FiDatabase,
  FiCpu,
  FiShield,
  FiSend,
  FiCheckCircle,
  FiThumbsUp,
  FiFileText,
  FiGitMerge,
  FiClipboard,
  FiDollarSign,
} from 'react-icons/fi';

// Reusable component for pillar cards
const PillarCard = ({ icon, title, description, colorScheme }) => (
  <Card shadow="xl" h="full" borderTop="4px" borderColor={`${colorScheme}.400`}>
    <CardHeader>
      <HStack>
        <Center w={16} h={16} bg={`${colorScheme}.100`} borderRadius="full">
          <Icon as={icon} boxSize={8} color={`${colorScheme}.600`} />
        </Center>
        <Heading size="md" color="gray.800">{title}</Heading>
      </HStack>
    </CardHeader>
    <CardBody>
      <Text color="gray.600">{description}</Text>
    </CardBody>
  </Card>
);

const IONMWorkflow = () => {
  return (
    <Grid
      templateAreas={`"header header" "main main"`}
      gridTemplateRows={'60px 1fr'}
      gridTemplateColumns={'1fr'}
      h='100vh'
      gap='1'
    >
      <HeaderMenus />
      <GridItem area={'main'} bg="gray.50" overflowY="auto">
        <Container maxW="7xl" py={16}>
          <VStack spacing={16} align="stretch">
            
            {/* 1. Headline Section */}
            <VStack spacing={6} textAlign="center">
              <Badge colorScheme="teal" fontSize="md" px={4} py={2} borderRadius="full">
                Solution Deep Dive: IONM
              </Badge>
              <Heading size="2xl" color="gray.800">The Comprehensive IONM Revenue Solution</Heading>
              <Text fontSize="xl" color="gray.600" maxW="5xl">
                For every advanced neurodiagnostic procedure your technology enables, Revedy provides the critical financial infrastructure to ensure your customers achieve maximum, compliant reimbursement. We transform their reimbursement uncertainty into your strategic advantage.
              </Text>
            </VStack>

            <Divider my={4} />

            {/* 2. Foundational Pillar */}
            <VStack spacing={8} align="stretch">
              <Heading size="xl" textAlign="center" color="gray.700">Pillar 0: The Unimpeachable Source of Truth</Heading>
              <Text fontSize="lg" color="gray.600" textAlign="center" maxW="4xl" mx="auto">
                Our process begins by creating a complete and validated record of the procedure, eliminating the discrepancies that lead to denials.
              </Text>
              <Card bg="white" shadow="2xl" borderWidth="1px" borderColor="gray.200">
                <CardBody p={10}>
                  <HStack spacing={8} flexDir={{ base: "column", md: "row" }}>
                    <Center w={24} h={24} bgGradient="linear(to-br, gray.600, gray.800)" color="white" borderRadius="full" shadow="lg">
                      <Icon as={FiGitMerge} boxSize={12} />
                    </Center>
                    <VStack align="start" spacing={4}>
                      <Heading size="lg" color="gray.800">Integrated Data Validation</Heading>
                      <List spacing={3} fontSize="lg">
                        <ListItem>
                          <ListIcon as={FiCheckCircle} color='green.500' />
                          <Text as="span" fontWeight="bold">Narrative + Device Data:</Text> We ingest the full case file—operative notes, technical reports, AND the raw, structured data output directly from the monitoring system.
                        </ListItem>
                        <ListItem>
                          <ListIcon as={FiCheckCircle} color='green.500' />
                          <Text as="span" fontWeight="bold">AI-Powered Cross-Validation:</Text> Our engine cross-references the physician's narrative against the electronic device data, ensuring every monitored modality is electronically verified.
                        </ListItem>
                        <ListItem>
                          <ListIcon as={FiCheckCircle} color='green.500' />
                          <Text as="span" fontWeight="bold">Audit-Proof Record:</Text> This creates a single, validated, and audit-proof record of the procedure, forming the foundation for all subsequent steps.
                        </ListItem>
                      </List>
                    </VStack>
                  </HStack>
                </CardBody>
              </Card>
            </VStack>

            <Divider my={4} />

            {/* 3. The Four Value Pillars */}
            <VStack spacing={8} align="stretch">
              <Heading size="xl" textAlign="center" color="gray.700">The Four Pillars of IONM Revenue Success</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
                <PillarCard
                  icon={FiClipboard}
                  colorScheme="blue"
                  title="1. Automated Coding & Claim Generation"
                  description="Our AI analyzes the validated case file to automatically assign the correct CPT codes for every modality (EEG, SSEP, MEP, EMG). It accurately calculates time-based units, counts nerve/screw tests, and generates separate, compliant professional (26) and technical (TC) claims, maximizing revenue capture while eliminating human error."
                />
                <PillarCard
                  icon={FiCpu}
                  colorScheme="purple"
                  title="2. Payer-Specific Code Optimization"
                  description="Our engine goes beyond standard coding by applying a layer of payer-specific rules. It automatically adjusts codes to meet the unique requirements of major payers like Medicare, UHC, and others—for example, substituting 95941 with G0453 or removing codes that are not reimbursed by a specific plan. This ensures the submitted claim is optimized for payment."
                />
                <PillarCard
                  icon={FiShield}
                  colorScheme="green"
                  title="3. Proactive Medical Necessity Validation"
                  description="Before submission, we automatically cross-reference the procedure and diagnosis codes against our vast library of Medicare (LCD/NCD) and commercial payer policies. This pre-submission check ensures the claim meets the payer's specific medical necessity criteria, drastically reducing a primary cause of IONM denials."
                />
                <PillarCard
                  icon={FiSend}
                  colorScheme="orange"
                  title="4. Intelligent Denial & Appeal Automation"
                  description="When a denial occurs, our system closes the loop. The AI analyzes the denial reason, retrieves the validated clinical evidence and device data from the case file, finds the relevant rule in the payer's own policy, and drafts a precise, evidence-based appeal that is overwhelmingly difficult for payers to dispute."
                />
              </SimpleGrid>
            </VStack>

            <Divider my={4} />

            {/* 4. The Result */}
            <VStack spacing={8}>
                <Heading size="xl" color="gray.700">The Result: A Strategic Partnership for Commercial Acceleration</Heading>
                <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="center" p={8} bg="white" shadow="lg" borderRadius="lg">
                    <Icon as={FiThumbsUp} boxSize={24} color="teal.500" mr={{ base: 0, md: 8 }} mb={{ base: 4, md: 0 }} />
                    <VStack align="start">
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                            By solving the reimbursement challenge, Revedy de-risks the purchase of Natus technology for hospitals.
                        </Text>
                        <Text fontSize="lg" color="gray.600">
                            This shortens sales cycles, boosts market adoption of your most advanced platforms, and directly accelerates your revenue growth.
                        </Text>
                    </VStack>
                </Flex>
            </VStack>

          </VStack>
        </Container>
      </GridItem>
    </Grid>
  );
};

export default IONMWorkflow;